import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import useAuth from 'hooks/useAuth';

// How long account grants may go stale before we re-fetch on the next neutral page.
const ACCOUNT_REFRESH_STALE_MS = 15 * 60 * 1000;

// "Neutral" pages have no in-progress work that a grant change could disrupt, so they
// are safe to refresh on. Add new neutral routes here — each predicate takes a pathname.
const NEUTRAL_ROUTE_MATCHERS = [
    (pathname) => pathname === '/', // Homepage
    (pathname) => /^\/clinic\/[^/]+\/patients$/.test(pathname), // Patient List
    (pathname) => /^\/clinic\/[^/]+\/tasks$/.test(pathname), // Tasks List
    (pathname) => /^\/clinic\/[^/]+\/vitals\/alerts$/.test(pathname) // Vitals Alerts List
];

const isNeutralPage = (pathname) => NEUTRAL_ROUTE_MATCHERS.some((matches) => matches(pathname));

// Staleness-gated account refresh: re-fetches /api/account/me (grants, clinics) only when
// the user lands on a neutral page and the data is older than ACCOUNT_REFRESH_STALE_MS.
// No blind interval — if the user sits on a heavy page nothing changes underneath them.
const useFreshAccountGrants = () => {
    const { refreshAccount } = useAuth();
    const { pathname } = useLocation();
    // Seed at mount: app init already fetched fresh data, so the clock starts now.
    const lastAccountLoadRef = useRef(Date.now());

    useEffect(() => {
        if (!isNeutralPage(pathname)) {
            return;
        }
        if (Date.now() - lastAccountLoadRef.current < ACCOUNT_REFRESH_STALE_MS) {
            return;
        }
        // Bump before firing so re-navigations don't double-fetch and a failed refresh waits
        // out the full window instead of retrying on every navigation. The window is a hint,
        // not a guarantee the data is at most ACCOUNT_REFRESH_STALE_MS old.
        lastAccountLoadRef.current = Date.now();
        refreshAccount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);
};

export default useFreshAccountGrants;
